import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Check, Calendar, Phone, Users, Clock, MapPin, Award } from 'lucide-react';
import { motion } from 'framer-motion';
export function HeroSection() {
  const {
    t
  } = useLanguage();
  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const stats = [{
    icon: Users,
    value: t('Thousands', 'Miles'),
    label: t('Patients Helped', 'Pacientes atendidos')
  }, {
    icon: Clock,
    value: '20+',
    label: t('Years Experience', 'Años de experiencia')
  }, {
    icon: MapPin,
    value: '2',
    label: t('Convenient Locations', 'Ubicaciones')
  }, {
    icon: Award,
    value: 'MD, FSSO',
    label: t('Board Certified', 'Certificado')
  }];
  return <section className="relative bg-[#0f172a] pt-28 sm:pt-32 md:pt-40 pb-0 overflow-hidden min-h-screen md:min-h-[90vh] flex flex-col justify-between" data-id="element-109">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5 pointer-events-none" data-id="element-110"></div>
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-[#0d9488] rounded-full mix-blend-multiply filter blur-[128px] opacity-10 animate-pulse" data-id="element-111"></div>
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-blue-900 rounded-full mix-blend-multiply filter blur-[128px] opacity-10" data-id="element-112"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 flex-grow flex flex-col justify-center" data-id="element-113">
        <div className="flex flex-col lg:flex-row items-center gap-8 sm:gap-12 lg:gap-20" data-id="element-114">
          {/* Text Content - Left 60% */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} className="flex-1 lg:w-[60%] text-center lg:text-left pt-4 lg:pt-0 w-full" data-id="element-115">
            <div className="inline-block mb-4 sm:mb-6 px-3 py-1.5 rounded-full bg-[#0d9488]/10 border border-[#0d9488]/30 backdrop-blur-sm" data-id="element-116">
              <span className="text-[#0d9488] text-xs font-bold tracking-[0.15em] uppercase" data-id="element-117">
                Lehigh Valley Weight Loss — Allentown, PA
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-extrabold text-white leading-[1.1] mb-4 sm:mb-6 tracking-tight" data-id="element-118">
              {t('Weight-Loss Surgery Care in Allentown, PA', 'Cirugía bariátrica en Allentown, PA')}
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-slate-300 mb-6 sm:mb-10 max-w-2xl mx-auto lg:mx-0 leading-relaxed font-light" data-id="element-119">
              {t('Maneesh Ailawadi, MD, FSSO — Expert General & Bariatric Surgeon at Lehigh Valley Weight Loss. Helping patients pursue better health with evidence-based surgical options.', 'Maneesh Ailawadi, MD, FSSO — Cirujano general y bariátrico en Lehigh Valley Weight Loss. Opciones quirúrgicas basadas en evidencia, con un plan claro y seguimiento.')}
            </p>

            <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-10 max-w-lg mx-auto lg:mx-0" data-id="element-120">
              {[t('Experienced bariatric & general surgeon', 'Experiencia en cirugía bariátrica y general'), t('Advanced training + research background', 'Formación avanzada e investigación'), t('Convenient Allentown location', 'Ubicación conveniente en Allentown')].map((item, index) => <div key={index} className="flex items-center justify-center lg:justify-start group" data-id="element-121">
                  <div className="bg-[#0d9488]/20 p-1.5 rounded-full mr-3 sm:mr-4 flex-shrink-0 group-hover:bg-[#0d9488] transition-colors duration-300" data-id="element-122">
                    <Check className="w-4 h-4 text-[#0d9488] group-hover:text-white transition-colors duration-300" data-id="element-123" />
                  </div>
                  <span className="text-slate-200 font-medium text-sm sm:text-base lg:text-lg text-left" data-id="element-124">
                    {item}
                  </span>
                </div>)}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-5 justify-center lg:justify-start mb-6 sm:mb-8" data-id="element-125">
              <a href="tel:4844084100" className="inline-flex items-center justify-center px-6 sm:px-8 py-3.5 sm:py-4 bg-[#0d9488] hover:bg-[#0f766e] text-white font-bold rounded-lg transition-all shadow-lg hover:shadow-[#0d9488]/40 hover:-translate-y-1 text-base sm:text-lg" data-id="element-126">
                <Phone className="w-5 h-5 mr-2 flex-shrink-0" data-id="element-127" />
                <span data-id="element-128">
                  {t('To schedule a consult please call (484) 408-4100', 'Para agendar una consulta llame al (484) 408-4100')}
                </span>
              </a>
            </div>

            <p className="text-xs text-slate-500 italic max-w-md mx-auto lg:mx-0" data-id="element-129">
              {t('* Eligibility and treatment options are determined during a medical evaluation.', '* La elegibilidad y el plan se determinan con una evaluación médica.')}
            </p>
          </motion.div>

          {/* Doctor Image - shown on all screens, right on desktop, below text on mobile */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: 'easeOut'
        }} className="flex-1 lg:w-[40%] relative w-full" data-id="element-130">
            <div className="relative w-full max-w-xs sm:max-w-sm md:max-w-md mx-auto aspect-[3/4]" data-id="element-131">
              <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a] via-transparent to-transparent z-10 bottom-0 h-32" data-id="element-132"></div>
              <div className="absolute -inset-4 border-2 border-[#0d9488]/30 rounded-2xl transform translate-x-4 translate-y-4 z-0" data-id="element-133"></div>
              <img src="https://cdn.magicpatterns.com/uploads/5Rk82FQzqcVJkW3VxhvpdW/M-Ailawadi.jpg" alt="Dr. Maneesh Ailawadi" className="w-full h-full object-cover rounded-2xl shadow-2xl relative z-10 border border-slate-700" data-id="element-134" />

              {/* Floating Badge */}
              <div className="absolute -bottom-4 left-2 sm:-bottom-6 sm:-left-6 z-20 bg-white p-3 sm:p-5 rounded-xl shadow-xl border-l-4 border-[#0d9488] max-w-[180px] sm:max-w-[220px]" data-id="element-135">
                <p className="text-[#0f172a] font-bold text-sm sm:text-lg leading-tight" data-id="element-136">
                  Dr. Maneesh Ailawadi
                </p>
                <p className="text-[#0d9488] text-xs sm:text-sm font-bold uppercase tracking-wider mt-1" data-id="element-137">
                  MD, FSSO
                </p>
                <p className="text-slate-500 text-xs mt-0.5" data-id="element-138">
                  Board Certified Surgeon
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="relative z-20 mt-12 sm:mt-16 bg-white border-t border-slate-100 shadow-lg" data-id="element-139">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-140">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-slate-100" data-id="element-141">
            {stats.map((stat, index) => <div key={index} className="py-6 sm:py-8 px-3 sm:px-4 flex flex-col items-center justify-center text-center" data-id="element-142">
                <stat.icon className="w-6 h-6 sm:w-8 sm:h-8 text-[#0d9488] mb-2 sm:mb-3 opacity-80" data-id="element-143" />
                <span className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-[#0f172a] mb-1" data-id="element-144">
                  {stat.value}
                </span>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide leading-tight" data-id="element-145">
                  {stat.label}
                </span>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}